import type { MouseEvent, ReactNode } from "react";
import { Button } from "../ui/Button.js";

export function SelectableSuggestionRow({
  id,
  checked,
  disabled = false,
  onCheckedChange,
  tone = "change",
  label = "Select suggestion",
  children,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
  tone?: "add" | "change" | "remove";
  /** Accessible name for the native selection checkbox. */
  label?: string;
  children: ReactNode;
}): JSX.Element {
  const toneClass =
    tone === "add"
      ? "border-success/30 bg-success/5"
      : tone === "remove"
        ? "border-danger/30 bg-danger/5"
        : "border-warning/30 bg-warning/5";

  function handleRowClick(event: MouseEvent<HTMLDivElement>) {
    if (disabled) return;
    const target = event.target as HTMLElement;
    if (target.closest("input, button, a, select, textarea, label")) return;
    onCheckedChange(!checked);
  }

  return (
    <div
      className={`flex items-start gap-2 rounded-md border p-2 transition ${toneClass} ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}
      onClick={handleRowClick}
    >
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function SuggestionSelectionToolbar({
  selectedCount,
  totalCount,
  disabled = false,
  onSelectAll,
  onClear,
}: {
  selectedCount: number;
  totalCount: number;
  disabled?: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-white px-3 py-2">
      <span className="mr-auto text-sm font-medium text-ink" aria-live="polite">
        {selectedCount} of {totalCount} selected
      </span>
      <Button size="sm" variant="secondary" disabled={disabled || selectedCount === totalCount} onClick={onSelectAll}>
        Select all
      </Button>
      <Button size="sm" variant="secondary" disabled={disabled || selectedCount === 0} onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
